import React, { useState, useEffect } from "react";
import { Edit2, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";
import ProductFormModal from "../../components/AdminSideComponent/Products/Productformmodal";
import DeleteConfirmModal from "../../components/AdminSideComponent/Common/Deleteconfirmmodal";
import axios from 'axios'
import toast from "react-hot-toast";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/products`; // Change this to your backend URL

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState(null); // 'add', 'edit', 'delete', null
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);

  // Fetch all products
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await axios.get(`${API_BASE_URL}/getAll`);
      // console.log("first" , data.data.data);
      setProducts(data.data.data || data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setModalType("add");
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setModalType("edit");
  };

  const handleDeleteProduct = (product) => {
    setSelectedProduct(product);
    setModalType("delete");
  };

  const handleCloseModal = () => {
    setModalType(null);
    setSelectedProduct(null);
  };

  const handleProductSaved = () => {
    fetchProducts();
    handleCloseModal();
  };

  const handleProductDeleted = () => {
    fetchProducts();
    handleCloseModal();
    toast.success("Product Deleted Successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Products</h1>
            <p className="text-gray-600 mt-2">Manage your product catalog</p>
          </div>
          <button
            onClick={handleAddProduct}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            <Plus size={20} />
            Add Product
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 text-lg">No products found</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {products.map((product) => (
              <div
                key={product._id}
                className="bg-white rounded-lg shadow hover:shadow-md transition border border-gray-200"
              >
                {/* Product Header */}
                <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-bold text-gray-900">
                        {product.heading}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          product.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {product.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-2">{product.tagline}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Category:{" "}
                      <code className="bg-gray-100 px-2 py-1 rounded">
                        {product.category}
                      </code>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition"
                      title="Edit"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition"
                      title="Delete"
                    >
                      <Trash2 size={20} />
                    </button>
                    <button
                      onClick={() =>
                        setExpandedProduct(
                          expandedProduct === product._id ? null : product._id
                        )
                      }
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                    >
                      {expandedProduct === product._id ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Product Details (Expandable) */}
                {expandedProduct === product._id && (
                  <div className="p-6 space-y-4 text-sm">
                    {product.overViewTagLine && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Overview Tagline:
                        </span>
                        <p className="text-gray-600">{product.overViewTagLine}</p>
                      </div>
                    )}

                    {product.capabilities?.length > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Capabilities:
                        </span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {product.capabilities.map((cap, idx) => (
                            <span
                              key={idx}
                              className="bg-blue-100 text-blue-800 px-3 py-1 rounded"
                            >
                              {cap}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.impact?.length > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Impact:
                        </span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {product.impact.map((imp, idx) => (
                            <span
                              key={idx}
                              className="bg-green-100 text-green-800 px-3 py-1 rounded"
                            >
                              {imp}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.integrations?.length > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Integrations:
                        </span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {product.integrations.map((int, idx) => (
                            <span
                              key={idx}
                              className="bg-purple-100 text-purple-800 px-3 py-1 rounded"
                            >
                              {int}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.metrics?.length > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">
                          Metrics:
                        </span>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {product.metrics.map((metric, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-100 p-2 rounded border border-gray-200"
                            >
                              <p className="font-semibold text-gray-900">
                                {metric.value}
                              </p>
                              <p className="text-gray-600">{metric.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {(modalType === "add" || modalType === "edit") && (
        <ProductFormModal
          mode={modalType}
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleProductSaved}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      {modalType === "delete" && selectedProduct && (
        <DeleteConfirmModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onDelete={handleProductDeleted}
          apiBaseUrl={API_BASE_URL}
        />
      )}
    </div>
  );
};

export default ProductList;